import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Pill, Plus, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useEncounter } from '@/contexts/EncounterContext';
import PrescriptionFormSheet from '@/components/clinic/PrescriptionFormSheet';
import { prescriptionService } from '@/services/prescriptionService';
import { cacheKeys } from '@/lib/cacheKeys';

interface VisitRxPanelProps {
  practitionerId: string;
}

const VisitRxPanel: React.FC<VisitRxPanelProps> = ({ practitionerId }) => {
  const { encounterId, appointmentId, patientId, isSigned } = useEncounter();
  const [showForm, setShowForm] = useState(false);

  const { data: rxResponse, isLoading, refetch } = useQuery({
    queryKey: cacheKeys.patientPrescriptions(patientId),
    queryFn: () => prescriptionService.getPrescriptionsByPatientId(patientId, {
      page: 1, limit: 50, sortBy: 'created_at', sortOrder: 'DESC',
    }),
    enabled: !!patientId,
  });

  const all: any[] = (rxResponse as any)?.data?.prescriptions
    || (rxResponse as any)?.data?.data
    || (rxResponse as any)?.data
    || [];
  const visitRx = all.filter((p: any) => p.encounter_id === encounterId);

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader className='flex-row items-center justify-between space-y-0'>
          <CardTitle className='text-base flex items-center gap-2'>
            <Pill className='h-4 w-4 text-emerald-700' />
            Prescriptions written this visit
          </CardTitle>
          {!isSigned && (
            <Button onClick={() => setShowForm(true)} size='sm'>
              <Plus className='mr-1 h-4 w-4' /> New prescription
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='flex justify-center py-4'>
              <Loader2 className='h-5 w-5 animate-spin text-slate-400' />
            </div>
          ) : visitRx.length === 0 ? (
            <p className='text-sm text-slate-500'>
              No prescriptions written for this visit yet.
            </p>
          ) : (
            <ul className='divide-y divide-slate-100'>
              {visitRx.map((rx: any) => (
                <li key={rx.id} className='flex items-start justify-between py-3'>
                  <div className='flex-1'>
                    <div className='flex flex-wrap items-center gap-2'>
                      {rx.medicationRequests?.slice(0, 3).map((m: any) => (
                        <Badge key={m.id} variant='outline' className='font-normal'>
                          {m.medication_name || m.medication}
                        </Badge>
                      ))}
                      {rx.medicationRequests?.length > 3 && (
                        <span className='text-xs text-slate-500'>
                          +{rx.medicationRequests.length - 3} more
                        </span>
                      )}
                    </div>
                    <div className='mt-1 text-xs text-slate-500'>
                      Status: {rx.status}
                      {rx.created_at && ` • ${new Date(rx.created_at).toLocaleString('en-US', {
                        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                      })}`}
                    </div>
                  </div>
                  <Badge
                    variant='outline'
                    className={
                      rx.status === 'verified' || rx.status === 'submitted'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-amber-200 bg-amber-50 text-amber-700'
                    }
                  >
                    {rx.status}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <PrescriptionFormSheet
        open={showForm}
        onOpenChange={setShowForm}
        onSubmit={() => {
          refetch();
        }}
        mode='create'
        patientId={patientId}
        practitionerId={practitionerId}
        // EncounterContext is already in the tree, but we pass explicit
        // props too so the linkage is unambiguous.
        encounterId={encounterId}
        appointmentId={appointmentId || undefined}
      />
    </div>
  );
};

export default VisitRxPanel;
